/** Offboarding, reactivation and other management actions. */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { DateInput } from "../../components/ui/controls.js";
import { ToggleSwitch as InputSwitch } from "@primereact/ui/toggleswitch";
import { apiDelete, apiPost } from "../../lib/api.js";
import { ConfirmButton, MutationError, Section } from "../shared.js";
import type { EmployeeProfileData } from "./types.js";

export function EmployeeLifecycle({ data }: { data: EmployeeProfileData }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [offboardNow, setOffboardNow] = useState(false);
  const [employmentEnd, setEmploymentEnd] = useState(
    data.profile?.employmentEndDate ?? new Date().toISOString().slice(0, 10),
  );
  const [reason, setReason] = useState("");
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["employee", data.user.id] }),
      client.invalidateQueries({ queryKey: ["employees"] }),
    ]);
  };
  const offboard = useMutation({
    mutationFn: () =>
      apiPost(`/employees/${data.user.id}/offboard`, {
        employmentEnd: offboardNow ? "now" : employmentEnd,
        reason,
      }),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: () => apiDelete(`/employees/${data.user.id}/offboard`),
    onSuccess: refresh,
  });
  const reactivate = useMutation({
    mutationFn: () => apiPost(`/employees/${data.user.id}/reactivate`),
    onSuccess: refresh,
  });
  return (
    <Section className="mt-5" title={t("employees.lifecycle")}>
      {data.user.status === "deactivated" ? (
        <div>
          <p className="mb-4 text-sm text-app-muted">
            {t("employees.deactivatedHelp")}
          </p>
          <Button
            onClick={() => reactivate.mutate()}
            loading={reactivate.isPending || undefined}
          >
            {t("employees.reactivate")}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {data.profile?.employmentEndDate ? (
            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-md border border-app-border p-3">
              <p className="text-sm text-app-muted">
                {t("employees.employmentEnd")}:{" "}
                <span className="font-medium text-app-text">
                  {data.profile.employmentEndDate}
                </span>
              </p>
              <Button
                variant="outlined"
                severity="secondary"
                onClick={() => cancel.mutate()}
                loading={cancel.isPending || undefined}
              >
                {t("common.cancel")}
              </Button>
            </div>
          ) : null}
          <Field label={t("employees.employmentEnd")} required={!offboardNow}>
            <div className="flex items-center gap-3">
              <InputSwitch.Root
                checked={offboardNow}
                onCheckedChange={(event: { checked: boolean }) =>
                  setOffboardNow(Boolean(event.checked))
                }
              >
                <InputSwitch.Control>
                  <InputSwitch.Handle />
                </InputSwitch.Control>
              </InputSwitch.Root>
              <span className="text-sm text-app-muted">
                {t("employees.offboardNow")}
              </span>
            </div>
            {!offboardNow ? (
              <DateInput
                value={employmentEnd}
                onChange={(value) => setEmploymentEnd(value)}
                className="mt-2 w-full"
                required
              />
            ) : null}
          </Field>
          <Field label={t("employees.offboardingReason")} required>
            <Input
              required
              value={reason}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setReason(event.target.value)
              }
            />
          </Field>
          <div className="sm:col-span-2">
            <ConfirmButton
              confirmMessage={t("employees.offboardConfirm")}
              onConfirm={() => offboard.mutate()}
            >
              {t("employees.offboard")}
            </ConfirmButton>
          </div>
        </div>
      )}
      <MutationError
        error={offboard.error ?? cancel.error ?? reactivate.error}
      />
    </Section>
  );
}
export function ManagementActionsPanel({
  data,
}: {
  data: EmployeeProfileData;
}) {
  return (
    <div className="space-y-5">
      <EmployeeLifecycle data={data} />
    </div>
  );
}
